import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  AlertTriangle, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  RefreshCw, 
  Search,
  TrendingUp,
  TrendingDown,
  Package,
  FileText,
  ThumbsUp,
  ThumbsDown,
  Info
} from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  pending: { label: 'Pending Approval', color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock },
  auto_approved: { label: 'Auto-Approved', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: CheckCircle2 },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800 border-red-200', icon: XCircle }
};

export function PODiscrepancyContent() {
  const [discrepancies, setDiscrepancies] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedDiscrepancy, setSelectedDiscrepancy] = useState(null);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalAction, setApprovalAction] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [discRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/po-discrepancies`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/po-discrepancies/summary/stats`, { headers: getAuthHeader() })
      ]);
      setDiscrepancies(discRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load discrepancy data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'dd/MM/yyyy HH:mm');
    } catch {
      return '-';
    }
  };

  // Filter discrepancies
  const filteredDiscrepancies = discrepancies.filter(d => {
    const matchesSearch = 
      d.discrepancy_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.po_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || d.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleApprovalClick = (discrepancy, action) => {
    setSelectedDiscrepancy(discrepancy);
    setApprovalAction(action);
    setApprovalNotes('');
    setApprovalDialogOpen(true);
  };

  const handleApprovalSubmit = async () => {
    if (approvalAction === 'reject' && !approvalNotes.trim()) {
      toast.error('Rejection reason is required');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/po-discrepancies/${selectedDiscrepancy.id}/approve`,
        { action: approvalAction, notes: approvalNotes },
        { headers: getAuthHeader() }
      );
      toast.success(`Discrepancy ${approvalAction === 'approve' ? 'approved' : 'rejected'} successfully`);
      setApprovalDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process approval');
    } finally {
      setSubmitting(false);
    }
  };

  const pendingDiscrepancies = discrepancies.filter(d => d.status === 'pending');

  return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-yellow-600" />
              PO Quantity Discrepancies
            </h1>
            <p className="text-muted-foreground mt-1">
              Review and approve quantity variances between PO and received goods
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">Total Records</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-yellow-200 bg-yellow-50/30">
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-sm text-yellow-700">Pending</p>
                  <p className="text-2xl font-bold text-yellow-800">{stats.pending}</p>
                  <p className="text-xs text-yellow-600">{formatCurrency(stats.pending_value)}</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-blue-200 bg-blue-50/30">
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-sm text-blue-700">Auto-Approved</p>
                  <p className="text-2xl font-bold text-blue-800">{stats.auto_approved}</p>
                  <p className="text-xs text-blue-600">≤{stats.tolerance_percent}% variance</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-green-200 bg-green-50/30">
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-sm text-green-700">Approved</p>
                  <p className="text-2xl font-bold text-green-800">{stats.approved}</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-red-200 bg-red-50/30">
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-sm text-red-700">Rejected</p>
                  <p className="text-2xl font-bold text-red-800">{stats.rejected}</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-purple-200 bg-purple-50/30">
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-sm text-purple-700">Tolerance</p>
                  <p className="text-2xl font-bold text-purple-800">{stats.tolerance_percent}%</p>
                  <p className="text-xs text-purple-600">Auto-approve limit</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Pending Alert */}
        {pendingDiscrepancies.length > 0 && (
          <Card className="border-yellow-300 bg-yellow-50">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-yellow-600" />
                <div>
                  <p className="font-medium text-yellow-800">
                    {pendingDiscrepancies.length} discrepanc{pendingDiscrepancies.length === 1 ? 'y' : 'ies'} awaiting approval
                  </p>
                  <p className="text-sm text-yellow-700">
                    Total value impact: {formatCurrency(pendingDiscrepancies.reduce((sum, d) => sum + d.total_value_impact, 0))}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder="Search by discrepancy no, PO or supplier..."
              className="pl-9"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="auto_approved">Auto-Approved</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Discrepancies Table */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Discrepancy Records</CardTitle>
            <CardDescription>
              {filteredDiscrepancies.length} records
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Discrepancy No</TableHead>
                    <TableHead>PO Number</TableHead>
                    <TableHead>Store Receipt</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead className="text-center">Items</TableHead>
                    <TableHead className="text-right">Max Variance</TableHead>
                    <TableHead className="text-right">Value Impact</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8">
                        <div className="flex items-center justify-center gap-2">
                          <RefreshCw className="w-5 h-5 animate-spin" />
                          Loading discrepancies...
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : filteredDiscrepancies.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-12">
                        <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-green-500 opacity-50" />
                        <p className="text-muted-foreground font-medium">No discrepancies found</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          All PO quantities match received quantities
                        </p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredDiscrepancies.map((disc) => {
                      const StatusIcon = statusConfig[disc.status]?.icon || Clock;
                      return (
                        <TableRow key={disc.id} className="hover:bg-muted/30">
                          <TableCell>
                            <Badge variant="outline" className="font-mono text-xs">
                              {disc.discrepancy_no}
                            </Badge>
                            <p className="text-xs text-muted-foreground mt-1">
                              {formatDate(disc.created_at)}
                            </p>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="font-mono text-xs bg-blue-50">
                              {disc.po_number}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="font-mono text-xs">
                              {disc.store_receipt_no}
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-[120px] truncate" title={disc.supplier_name}>
                            {disc.supplier_name}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="secondary">{disc.items?.length || 0}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <span className={`font-bold ${disc.max_discrepancy_percent > 3 ? 'text-red-600' : 'text-yellow-600'}`}>
                              {disc.max_discrepancy_percent?.toFixed(1)}%
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <span className={disc.total_value_impact >= 0 ? 'text-red-600' : 'text-green-600'}>
                              {formatCurrency(disc.total_value_impact)}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge className={`${statusConfig[disc.status]?.color} flex items-center gap-1 justify-center`}>
                              <StatusIcon className="w-3 h-3" />
                              {statusConfig[disc.status]?.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setSelectedDiscrepancy(disc)}
                                title="View Details"
                              >
                                <Info className="w-4 h-4" />
                              </Button>
                              {disc.status === 'pending' && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                    onClick={() => handleApprovalClick(disc, 'approve')}
                                    title="Approve"
                                  >
                                    <ThumbsUp className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => handleApprovalClick(disc, 'reject')}
                                    title="Reject"
                                  >
                                    <ThumbsDown className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Details Dialog */}
        <Dialog open={!!selectedDiscrepancy && !approvalDialogOpen} onOpenChange={() => setSelectedDiscrepancy(null)}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Discrepancy Details - {selectedDiscrepancy?.discrepancy_no}
              </DialogTitle>
              <DialogDescription>
                PO: {selectedDiscrepancy?.po_number} | Receipt: {selectedDiscrepancy?.store_receipt_no}
              </DialogDescription>
            </DialogHeader>
            
            {selectedDiscrepancy && (
              <div className="space-y-4">
                {/* Summary */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="bg-muted/50 p-3 rounded-lg">
                    <p className="text-xs text-muted-foreground">Supplier</p>
                    <p className="font-medium">{selectedDiscrepancy.supplier_name}</p>
                  </div>
                  <div className="bg-muted/50 p-3 rounded-lg">
                    <p className="text-xs text-muted-foreground">Max Variance</p>
                    <p className={`font-bold ${selectedDiscrepancy.max_discrepancy_percent > 3 ? 'text-red-600' : 'text-yellow-600'}`}>
                      {selectedDiscrepancy.max_discrepancy_percent?.toFixed(2)}%
                    </p>
                  </div>
                  <div className="bg-muted/50 p-3 rounded-lg">
                    <p className="text-xs text-muted-foreground">Value Impact</p>
                    <p className={`font-bold ${selectedDiscrepancy.total_value_impact >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {formatCurrency(selectedDiscrepancy.total_value_impact)}
                    </p>
                  </div>
                  <div className="bg-muted/50 p-3 rounded-lg">
                    <p className="text-xs text-muted-foreground">Status</p>
                    <Badge className={statusConfig[selectedDiscrepancy.status]?.color}>
                      {statusConfig[selectedDiscrepancy.status]?.label}
                    </Badge>
                  </div>
                </div>

                {/* Items Table */}
                <div>
                  <h4 className="font-medium mb-2">Discrepancy Items</h4>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>Item</TableHead>
                        <TableHead className="text-right">PO Qty</TableHead>
                        <TableHead className="text-right">Received</TableHead>
                        <TableHead className="text-right">Difference</TableHead>
                        <TableHead className="text-right">Variance %</TableHead>
                        <TableHead className="text-center">Type</TableHead>
                        <TableHead className="text-right">Value Impact</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedDiscrepancy.items?.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{item.item_name}</TableCell>
                          <TableCell className="text-right">{formatNumber(item.po_qty)} {item.unit}</TableCell>
                          <TableCell className="text-right">{formatNumber(item.received_qty)} {item.unit}</TableCell>
                          <TableCell className="text-right font-medium">
                            <span className={item.discrepancy_qty >= 0 ? 'text-red-600' : 'text-green-600'}>
                              {item.discrepancy_qty >= 0 ? '+' : ''}{formatNumber(item.discrepancy_qty)}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <span className={item.discrepancy_percent > 3 ? 'text-red-600 font-bold' : 'text-yellow-600'}>
                              {item.discrepancy_percent?.toFixed(2)}%
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline" className={item.discrepancy_type === 'over_receipt' ? 'bg-red-50 text-red-700' : 'bg-orange-50 text-orange-700'}>
                              {item.discrepancy_type === 'over_receipt' ? (
                                <><TrendingUp className="w-3 h-3 mr-1" /> Over</>
                              ) : (
                                <><TrendingDown className="w-3 h-3 mr-1" /> Short</>
                              )}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            <span className={item.value_impact >= 0 ? 'text-red-600' : 'text-green-600'}>
                              {formatCurrency(item.value_impact)}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Approval Info */}
                {selectedDiscrepancy.status !== 'pending' && (
                  <div className="bg-muted/30 p-4 rounded-lg">
                    <h4 className="font-medium mb-2">Approval Details</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Processed by:</span>
                        <span className="ml-2 font-medium">{selectedDiscrepancy.approved_by_name}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Date:</span>
                        <span className="ml-2">{formatDate(selectedDiscrepancy.approval_date)}</span>
                      </div>
                      {selectedDiscrepancy.approval_notes && (
                        <div className="col-span-2">
                          <span className="text-muted-foreground">Notes:</span>
                          <span className="ml-2">{selectedDiscrepancy.approval_notes}</span>
                        </div>
                      )}
                      {selectedDiscrepancy.rejection_reason && (
                        <div className="col-span-2">
                          <span className="text-muted-foreground">Rejection Reason:</span>
                          <span className="ml-2 text-red-600">{selectedDiscrepancy.rejection_reason}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Action Buttons for Pending */}
                {selectedDiscrepancy.status === 'pending' && (
                  <DialogFooter className="gap-2">
                    <Button
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => handleApprovalClick(selectedDiscrepancy, 'reject')}
                    >
                      <ThumbsDown className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                    <Button
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => handleApprovalClick(selectedDiscrepancy, 'approve')}
                    >
                      <ThumbsUp className="w-4 h-4 mr-2" />
                      Approve
                    </Button>
                  </DialogFooter>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Approval Confirmation Dialog */}
        <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {approvalAction === 'approve' ? (
                  <><CheckCircle2 className="w-5 h-5 text-green-600" /> Approve Discrepancy</>
                ) : (
                  <><XCircle className="w-5 h-5 text-red-600" /> Reject Discrepancy</>
                )}
              </DialogTitle>
              <DialogDescription>
                {selectedDiscrepancy?.discrepancy_no} - Max variance: {selectedDiscrepancy?.max_discrepancy_percent?.toFixed(2)}%
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">
                  {approvalAction === 'approve' ? 'Approval Notes (Optional)' : 'Rejection Reason (Required)'}
                </label>
                <Textarea
                  placeholder={approvalAction === 'approve' 
                    ? 'Add any notes about this approval...' 
                    : 'Please provide a reason for rejection...'
                  }
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  className="mt-1"
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setApprovalDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleApprovalSubmit}
                disabled={submitting}
                className={approvalAction === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
              >
                {submitting ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                ) : approvalAction === 'approve' ? (
                  <><ThumbsUp className="w-4 h-4 mr-2" /> Confirm Approval</>
                ) : (
                  <><ThumbsDown className="w-4 h-4 mr-2" /> Confirm Rejection</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
  );
}

// Standalone page wrapper
export default function PODiscrepancyPage() {
  return (
    <DashboardLayout>
      <PODiscrepancyContent />
    </DashboardLayout>
  );
}
